import { DateRange } from '@/utils/dateutils';
import { subDays } from 'date-fns';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDebounce } from '../debounce';
import { useDeleteRoleMutation, useGetRolesQuery } from '@/store/api/rolesApi';
import { showToastMessage } from '@/utils';
import { handleApiError } from '@/store/api/apiErrorHandler';
import { Role } from '@/types/rolesTypes';
import { useApiSorting } from '@/hooks/useApiSorting';

const sortKeyByColumnId: Record<string, string> = {
  name: 'name',
  description: 'description',
  createdAt: 'createdAt',
};

const useRoles = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [rolesData, setRolesData] = useState<Role[]>([]);
  const [search, setSearch] = useState('');
  const [activeModal, setActiveModal] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<number>();
  const debouncedSearch = useDebounce(search);
  const today = new Date();
  const [appliedRange, setAppliedRange] = useState<DateRange | undefined>({
    from: subDays(today, 6),
    to: today,
  });
  const [selectedRange, setSelectedRange] = useState<DateRange | undefined>({
    from: subDays(today, 6),
    to: today,
  });
  const [deleteRole] = useDeleteRoleMutation();
  const closeModal = useCallback(() => setActiveModal(null), []);
  const resetPage = useCallback(() => {
    setCurrentPage(1);
  }, []);
  const { sorting, handleSortingChange, sortParams } = useApiSorting(sortKeyByColumnId, resetPage);
  const queryParams = useMemo(
    () => ({
      page: currentPage,
      limit: pageSize,
      search: debouncedSearch.trim(),
      ...sortParams,
    }),
    [currentPage, pageSize, debouncedSearch, sortParams]
  );
  /* -------------------- API -------------------- */

  const { data, isLoading } = useGetRolesQuery(queryParams);
  useEffect(() => {
    setRolesData(data?.data || []);
  }, [data]);
  const handleDeleteRole = useCallback(
    async (id: string) => {
      try {
        setRolesData((prev) => prev.filter((item) => Number(item.id) !== Number(id)));
        const res = await deleteRole(Number(id)).unwrap();
        closeModal();
        showToastMessage('success', 'role deleted!');
      } catch (err) {
        handleApiError(err, 'Failed to delete role');
      }
    },
    [deleteRole]
  );
  const totalItems = data?.pagination.totalItems || 0;

  return {
    activeModal,
    setActiveModal,
    deleteId,
    setDeleteId,
    closeModal,
    selectedRange,
    appliedRange,
    setSelectedRange,
    setAppliedRange,
    setCurrentPage,
    search,
    setSearch,
    resetPage,
    setPageSize,
    pageSize,
    totalItems,
    currentPage,
    rolesData,
    isLoading,
    handleDeleteRole,
    sorting,
    handleSortingChange,
  };
};

export default useRoles;
